import * as Draft from 'draft-js';
import { action, computed, makeObservable, observable } from 'mobx';

import { FormFieldEntity } from 'entities/FormField';
import { ILocalStore, IServerEntity } from 'stores/interfaces';

import { ALL_DECORATORS } from './decorators';
import { EditorEntityKind } from './editorEntityKind';
import { editorStateFromServerString } from './editorStateFromServerString';
import { getAllEntities, GetAllEntitiesResultItem } from './getAllEntities';
import { API_FORMULA_START, normalizeToServerString } from './normalize';
import { NewFormulaModelParams } from './params';

type PrivateFields =
  | '_editorState'
  | '_allFormFieldEntities'
  | '_removeEntity'
  | '_setSelectionPosition'
  | 'addTextAtSelection';

export default class NewFormulaModel
  implements ILocalStore, IServerEntity<string>
{
  constructor(params: NewFormulaModelParams) {
    this._params = params;

    this._editorState = editorStateFromServerString({
      serverString: API_FORMULA_START,
      getLabelById: params.getLabelByFormFieldEntityId,
      onRemoveEntity: (id: string) => this._removeEntity(id),
    });

    makeObservable<NewFormulaModel, PrivateFields>(this, {
      _editorState: observable.ref,
      editorState: computed,
      setEditorState: action.bound,

      _allFormFieldEntities: computed,
      usedFormFieldIds: computed,

      _removeEntity: action.bound,
      _setSelectionPosition: action,
      addTextAtSelection: action,

      reset: action.bound,
      focusToEnd: action.bound,

      fromServerFormat: action,
    });
  }

  private readonly _params: NewFormulaModelParams;

  private _editorState: Draft.EditorState;

  // Состояние editor js
  get editorState(): Draft.EditorState {
    return this._editorState;
  }

  /**
   * Уникальные ID колонок таблицы, которые участвуют в формуле
   */
  get usedFormFieldIds(): string[] {
    return [
      ...new Set(
        this._allFormFieldEntities
          .map(({ entity }) => entity.getData().fieldId)
          .filter(Boolean)
      ),
    ];
  }

  get toServerFormat(): string {
    return normalizeToServerString(
      Draft.convertToRaw(this._editorState.getCurrentContent())
    );
  }

  /**
   * Список всех Draft.js-сущность, которые используются в формуле
   */
  private get _allFormFieldEntities(): GetAllEntitiesResultItem[] {
    return getAllEntities(this._editorState, EditorEntityKind.formField).filter(
      (entity) => entity.entity.getType() === EditorEntityKind.formField
    );
  }

  setEditorState(editorState: Draft.EditorState) {
    this._editorState = editorState;
  }

  /**
   * Вместо выделенного текста или на позицию курсора вставляется ссылка на колонку таблицы
   */
  addEntity(formFieldEntity: FormFieldEntity): void {
    /* Создаем Draft.js-сущность с данными */
    const contentStateWithEntity = this._editorState
      .getCurrentContent()
      .createEntity(EditorEntityKind.formField, 'IMMUTABLE', {
        fieldId: formFieldEntity.id,
        onRemoveEntity: this._removeEntity,
      });

    /* Получаем уникальный ключ Entity */
    const entityKey = contentStateWithEntity.getLastCreatedEntityKey();

    /* Объединяем текущее состояние с новым */
    const editorStateWithEntity = Draft.EditorState.set(this._editorState, {
      currentContent: contentStateWithEntity,
      decorator: ALL_DECORATORS,
    });

    this.addTextAtSelection({
      text: formFieldEntity.fieldData.name,
      entityKey,
      newEditorState: editorStateWithEntity,
    });
  }

  /**
   * Вставляет текст в состояние draft на место выделения
   */
  addTextAtSelection({
    text,
    entityKey,
    newEditorState,
  }: {
    /** Текст, который вставляем */
    text: string;
    /**
     * Ключ Draft.js-сущности, с которой нужно связать данный текст.
     * — Если параметр не передан, считаем, что text не связан ни с какой сущностью — просто текст
     **/
    entityKey?: string;
    /**
     * Состояние редактора, в которое нужно подмешать новый текст.
     * — Если параметр не передан, берем состояние редактора из this.
     **/
    newEditorState?: Draft.EditorState;
  }): void {
    const actualEditorState = newEditorState || this._editorState;

    const newContentState = Draft.Modifier.replaceText(
      actualEditorState.getCurrentContent(),
      actualEditorState.getSelection(),
      text,
      undefined,
      entityKey
    );

    this._editorState = Draft.EditorState.set(actualEditorState, {
      currentContent: newContentState,
      decorator: ALL_DECORATORS,
    });

    // После вставки текста, устанавливаем курсор на позицию справа от текста
    this._setSelectionPosition({
      newOffset:
        this._editorState.getSelection().getAnchorOffset() + text.length,
    });
  }

  /** Выделить текст в редакторе */
  private _setSelectionPosition({
    newFocus,
    newOffset,
  }: {
    /** Позиция, от которой начинается выделение */
    newOffset: number;
    /**
     * Позиция на которой остается фокус
     * — Если параметр не передан, фокус === смещение -> просто поставили курсор без выделения
     **/
    newFocus?: number;
  }): void {
    const currentSelection = this._editorState.getSelection();

    this._editorState = Draft.EditorState.forceSelection(
      this._editorState,
      currentSelection.merge({
        anchorOffset: newOffset,
        focusOffset: newFocus ?? newOffset,
      })
    );
  }

  /**
   * Удаление поля по клику на крестик в теге
   */
  private _removeEntity(draftEntityId: string): void {
    const foundEntity = this._allFormFieldEntities.find(
      (item) => item.entityKey === draftEntityId
    );

    if (!foundEntity) {
      return;
    }

    const startKey = this._editorState.getSelection().getStartKey();

    const block = this._editorState
      .getCurrentContent()
      .getBlockForKey(startKey);

    const mySelection = Draft.SelectionState.createEmpty(block.getKey());

    const updatedSelection = mySelection.merge({
      anchorOffset: foundEntity.start,
      focusOffset: foundEntity.end,
    });

    const newContentState = Draft.Modifier.removeRange(
      this._editorState.getCurrentContent(),
      updatedSelection,
      'forward'
    );

    this._editorState = Draft.EditorState.set(this._editorState, {
      currentContent: newContentState,
    });

    // После удаления куска текста, ставим курсор слева от текста
    this._setSelectionPosition({
      newOffset: updatedSelection.getAnchorOffset(),
    });
  }

  fromServerFormat(serverFormulaString: string): void {
    this._editorState = editorStateFromServerString({
      serverString: serverFormulaString,
      getLabelById: this._params.getLabelByFormFieldEntityId,
      onRemoveEntity: this._removeEntity,
    });
  }

  /** Установить курсор в конец текста */
  focusToEnd(): void {
    this._editorState = Draft.EditorState.moveFocusToEnd(this._editorState);
  }

  /** Очистить текст */
  reset(): void {
    this.fromServerFormat('=');
    // Ставим курсор в начало
    this._setSelectionPosition({ newOffset: 1 });
  }

  destroy(): void {
    // do nothing
  }
}
